// ── Turning pages from the keyboard ─────────────────────────────────────────
// ← and → turn one page. Holding the key keeps turning, slowly at first and
// then faster, the way thumbing through a book speeds up. The browser's own
// key repeat is ignored: its rate is set by the operating system and starts
// fast, so we run our own timer from the first keydown to the keyup.

// Delay after turn number `step` (0 is the press itself) before the next.
// 400 ms before the first repeat, then 300, 250, 200 and 150 from then on.
export function repeatDelay(step) {
  if (step <= 0) return 400;
  return Math.max(150, 350 - 50 * step);
}

// How long turn number `step` animates. A single press gets the full slide;
// repeats finish well inside their interval so the pages never queue up.
export function turnDuration(step) {
  if (!step) return 300;
  return Math.max(110, repeatDelay(step) - 60);
}

export function arrowDirection(key) {
  if (key === 'ArrowRight' || key === 'Right') return 1;
  if (key === 'ArrowLeft' || key === 'Left') return -1;
  return 0;
}

// Focused controls that use the arrow keys themselves: the <audio> element
// seeks with them, sliders and selects move, text fields move the caret.
const ARROW_TAKERS = 'input, textarea, select, audio, video, [contenteditable=""], [contenteditable="true"], '
  + '[role="slider"], [role="listbox"], [role="menu"], [role="tablist"], [role="radiogroup"]';

// Anything open over the page that the arrows should not reach past: the note
// editor, the selection toolbar, refs popups and sheets, the reading-options
// menu, the page-turn style menu, search, and the chapter scrubber while it
// is open.
export const OVERLAY_SELECTOR = '[aria-modal="true"], .anno-toolbar, .anno-note-editor, .bible-ref-popup, '
  + '.mobile-sheet, .bible-view-menu.open, .search-overlay, .chapter-scrub.open, '
  + '.account-panel, .lang-select-menu';

// Whether a keydown should be left alone rather than turn the page.
export function shouldIgnoreKey(e, doc = document) {
  if (!e || e.defaultPrevented) return true;
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return true;
  const el = e.target;
  if (el && el.nodeType === 1) {
    if (el.isContentEditable) return true;
    if (el.closest?.(ARROW_TAKERS)) return true;
  }
  if (doc.querySelector?.(OVERLAY_SELECTOR)) return true;
  // A text selection is being worked on (its toolbar is about to show).
  const sel = doc.defaultView?.getSelection?.();
  if (sel && !sel.isCollapsed && String(sel).trim()) return true;
  return false;
}

// Press-and-hold scheduler. onStep(dir, step) turns a page and returns false
// when it could not (the last chapter), which ends the run. The timer
// functions are injectable so tests can drive it with fake timers.
export function createKeyRepeater({ onStep, delay = repeatDelay, setTimer = setTimeout, clearTimer = clearTimeout }) {
  let held = 0;
  let step = 0;
  let timer = null;
  const clear = () => { if (timer != null) clearTimer(timer); timer = null; };
  const tick = () => {
    timer = null;
    if (!held) return;
    step += 1;
    if (onStep(held, step) === false) { held = 0; return; }
    timer = setTimer(tick, delay(step));
  };
  return {
    press(dir) {
      if (!dir || held === dir) return;
      clear();
      held = dir;
      step = 0;
      if (onStep(dir, 0) === false) { held = 0; return; }
      timer = setTimer(tick, delay(0));
    },
    // Releasing the other arrow does not stop a run of this one.
    release(dir) {
      if (dir && dir !== held) return;
      held = 0;
      clear();
    },
    stop() { held = 0; clear(); },
    get held() { return held; },
  };
}
